import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ProjectTeamConflict } from '@app/cem/core-data/project-team/project-team-conflict';
import { PxdAgGridSelectEditorComponent } from '@app/shared/aggrid/pxd-ag-grid-select-editor';
import { HeaderComponent } from '@app/shared/components/header/header.component';
import { PxdColumns } from '@app/shared/kendoui/grid/interfaces/pxd-columns';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { DefaultProjectTeamMaintenanceView } from '@app/shared/models/default-project-team-maintenance-view';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DialogActionsComponent, DialogComponent } from '@progress/kendo-angular-dialog';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { ExcelModule, GridComponent, GridDataResult, GridModule, RowArgs, SelectionEvent } from '@progress/kendo-angular-grid';
import { process, State } from '@progress/kendo-data-query';
import { AgGridAngular } from 'ag-grid-angular';
import { EmployeeVM } from 'app/cem/project/models/employee-vm';
import { GeneralProjectService } from 'app/cem/project/services/general-project.service';
import { ProjectTeamService } from 'app/cem/project/services/project-team.service';
import { AssetTeam, DefaultProjectTeam, Employee, Function } from 'app/shared/models';
import { Observable, Subject } from 'rxjs';
import { columns, DefaultTeamsData, ProjectTeamGridOptions } from './columns';

@Component({
  selector: 'app-project-team',
  templateUrl: './project-team.component.html',
  styleUrls: ['./project-team.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AgGridAngular,
    ButtonDirective,
    DialogActionsComponent,
    DialogComponent,
    ExcelModule,
    GridModule,
    HeaderComponent,
  ],
})
export class ProjectTeamComponent implements OnDestroy {
  public projectTeamService = inject(ProjectTeamService);
  public formBuilder = inject(UntypedFormBuilder);
  private generalProjectService = inject(GeneralProjectService);
  private cdr = inject(ChangeDetectorRef);

  private readonly agGrid = viewChild(AgGridAngular);
  private readonly grid = viewChild(GridComponent);

  public projectTeamGridOptions: ProjectTeamGridOptions = new ProjectTeamGridOptions();
  public icons = kendoIcons;

  private unsubscribe$ = new Subject<void>();
  private editedRowIndex: number;
  private newInQueue = false;
  public new = false;
  public loading = true;
  public editMode: boolean = false;
  public teamsView: Observable<GridDataResult>;
  public gridState: State = { skip: 0, take: 500 };
  public formGroup: UntypedFormGroup;
  public employeeList: Employee[];
  public filteredProgramMgrList = new Array<EmployeeVM>();
  public filteredProjectMgrList = new Array<EmployeeVM>();
  public filteredProjectControlList = new Array<EmployeeVM>();
  public filteredProjectEngineerList = new Array<EmployeeVM>();
  public filteredProjectTechList = new Array<EmployeeVM>();
  public assetTeamList: AssetTeam[];
  public functionList: Function[];
  public employeeMap = {};
  public assetTeamMap = {};
  public functionMap = {};
  public openConflicts: boolean = false;
  public conflictingProjects: ProjectTeamConflict[] = [];
  public conflictGridState: State = { skip: 0, take: 5 };
  public conflictDetailGridState: State = { skip: 0, take: 5 };
  public openedWarning: boolean = false;
  public group: any[] = [{ field: 'ProjectTeam' }, { field: 'ApproverName' }];
  public currentRecordUnderEdit: any;
  public canEdit: boolean = true;
  public selectedKeys: Array<number> = [];

  public columns: PxdColumns[] = columns;

  public openedTeamDialogError: boolean = false;
  public teamDialogMessage: string;
  public selected: RowArgs[] = [];
  public editedTeams: Map<number, DefaultTeamsData> = new Map<any, DefaultTeamsData>();
  public showValidationMessage: boolean = false;
  public validationMessage: string = '';

  public get isInEditingMode(): boolean {
    return this.editedRowIndex !== undefined || this.new;
  }

  columnDefs = [
    { field: 'Description' },
    {
      field: 'AssetTeamId', headerName: 'Asset Team',
      valueGetter: params => {
        return params.data.AssetTeamCode;
      },
      cellEditorSelector: params => {
        return {
          component: PxdAgGridSelectEditorComponent,
          params: {
            values: Array.from(this.assetTeamList).map(d => {
              return { value: d.AssetTeamId, text: d.AssetTeamCode };
            })
          }
        };
      },

    },
    {
      field: 'FunctionId', headerName: 'Function',
      valueGetter: params => {
        return params.data.FunctionDescription;
      },
      cellEditorSelector: params => {
        return {
          component: PxdAgGridSelectEditorComponent,
          params: {
            values: Array.from(this.functionList).map(d => {
              return { value: d.FunctionId, text: d.Description };
            })
          }
        };
      },

    },
    {
      field: 'ProgramManagerEmployeeId', headerName: 'Program Manager',
      valueGetter: params => {
        return this.renderData(params.data.ProgramManagerEmployeeId);
      },
      cellEditorSelector: params => {
        return {
          component: PxdAgGridSelectEditorComponent,
          params: {
            values: Array.from(this.projectTeamService.filterEmployeesByActiveV2(this.employeeList)
              .filter(emp => this.isNotContractor(emp.EmployeeId))).map(d => {
                return { value: d.EmployeeId, text: d.FullName };
              })
          }
        };
      }
    },
    {
      field: 'ProjectManagerEmployeeId', headerName: 'Project Manager',
      valueGetter: params => {
        return this.renderData(params.data.ProjectManagerEmployeeId);
      },
      cellEditorSelector: params => {
        return {
          component: PxdAgGridSelectEditorComponent,
          params: {
            values: Array.from(this.projectTeamService.filterEmployeesByActiveV2(this.employeeList)).map(d => {
              return { value: d.EmployeeId, text: d.FullName };
            })
          }
        };
      }
    },
    {
      field: 'ProjectControlAnalystEmployeeId', headerName: 'Project Control Analyst',
      valueGetter: params => {
        return this.renderData(params.data.ProjectControlAnalystEmployeeId);
      },
      cellEditorSelector: params => {
        return {
          component: PxdAgGridSelectEditorComponent,
          params: {
            values: Array.from(this.projectTeamService.filterEmployeesByActiveV2(this.employeeList)).map(d => {
              return { value: d.EmployeeId, text: d.FullName };
            })
          }
        };
      }
    },
    {
      field: 'ProjectEngineerEmployeeId', headerName: 'Project Engineer',
      valueGetter: params => {
        return this.renderData(params.data.ProjectEngineerEmployeeId);
      },
      cellEditorSelector: params => {
        return {
          component: PxdAgGridSelectEditorComponent,
          params: {
            values: Array.from(this.projectTeamService.filterEmployeesByActiveV2(this.employeeList)).map(d => {
              return { value: d.EmployeeId, text: d.FullName };
            })
          }
        };
      }
    },
    {
      field: 'ProjectTechEmployeeId', headerName: 'Project Tech',
      valueGetter: params => {
        return this.renderData(params.data.ProjectTechEmployeeId);
      },
      cellEditorSelector: params => {
        return {
          component: PxdAgGridSelectEditorComponent,
          params: {
            values: Array.from(this.projectTeamService.filterEmployeesByActiveV2(this.employeeList)
              .filter(emp => this.isNotContractor(emp.EmployeeId))).map(d => {
                return { value: d.EmployeeId, text: d.FullName };
              })
          }
        };
      }
    },
    {
      field: 'InUseCount', headerName: '', editable: false,
      cellRenderer: params => {
        if (params.value == 0 || !params.value)
          return '';
        else
          return `In Use (${params.value})`;
      }
    }

  ];

  constructor() {
    this.createFormGroup = this.createFormGroup.bind(this);
    this.excelExportAllData = this.excelExportAllData.bind(this);
    //this.loadEmployees();
    this.loadAssetTeams();
    this.loadFunctions();
  }

  public ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private loadTeams() {
    this.loading = true;
    this.generalProjectService.geProjectTeamsMaintenanceView()
      .then((data) => {
        this.agGrid().api.setGridOption('columnDefs', this.columnDefs);
        this.agGrid().api.setGridOption('rowData', data);
        this.loading = false;
      });
  }

  private loadEmployees() {
    this.generalProjectService.getEmployees()
      .then((data) => {
        this.employeeList = data;
        this.employeeMap = new Map();
        this.employeeList.map((x) => this.employeeMap[x.EmployeeId] = new EmployeeVM(x));
        this.loadTeams();
      });
  }

  private loadAssetTeams() {
    this.generalProjectService.getAssetTeams()
      .then((data) => {
        this.assetTeamList = data;
        this.assetTeamMap = new Map();
        this.assetTeamList.map((x) => this.assetTeamMap[x.AssetTeamId] = x);
      });
  }
  getDefaulAssetTeam = (id) => {
    if (!id) return '';
    let dteam = this.assetTeamMap[id];
    if (dteam) return dteam.AssetTeamCode;
    return '';
  }

  private loadFunctions() {
    this.generalProjectService.getFunctions()
      .then((data) => {
        this.functionList = data;
        this.functionMap = new Map();
        this.functionList.map((x) => this.functionMap[x.FunctionId] = x);
      });
  }
  getDefaulFunction = (id) => {
    if (!id) return '';
    let dt = this.functionMap[id];
    if (dt) return dt.Description;
    return '';
  }

  private markFormGroupTouched(formGroup: UntypedFormGroup) {
    (<any>Object).values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control.controls) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private renderData(val: number) {
    if (this.employeeList) {
      let emp = this.employeeList.filter(x => x.EmployeeId == val)[0];
      if (emp)
        return emp.LastName + ', ' + emp.PreferredFirstName;
      else
        return '';
    }
  }

  private loadFilteredLists(dataItem) {
    this.filteredProgramMgrList.push(new EmployeeVM(this.employeeList.find(employee => { return employee.EmployeeId === dataItem.ProgramManagerEmployeeId; })));
    this.filteredProjectMgrList.push(new EmployeeVM(this.employeeList.find(employee => { return employee.EmployeeId === dataItem.ProjectManagerEmployeeId; })));
    this.filteredProjectControlList.push(new EmployeeVM(this.employeeList.find(employee => { return employee.EmployeeId === dataItem.ProjectControlAnalystEmployeeId; })));
    this.filteredProjectEngineerList.push(new EmployeeVM(this.employeeList.find(employee => { return employee.EmployeeId === dataItem.ProjectEngineerEmployeeId; })));
    this.filteredProjectTechList.push(new EmployeeVM(this.employeeList.find(employee => { return employee.EmployeeId === dataItem.ProjectTechEmployeeId; })));
  }

  cancelEdit() {
    this.editedTeams = new Map<any, DefaultTeamsData>();
    this.loadTeams();
  }

  onCellValueChanged(e) {
    this.trackChanges(e);
  }

  onRowEditStopped(e) {

  }

  addNewTeam() {
    if (this.isFormValid()) {
      let defaultTeam: DefaultTeamsData = {
        DefaultProjectTeamId: -1,
        Description: '',
        AssetTeamId: null,
        FunctionId: null,
        ProgramManagerEmployeeId: null,
        ProjectManagerEmployeeId: null,
        ProjectControlAnalystEmployeeId: null,
        ProjectEngineerEmployeeId: null,
        ProjectTechEmployeeId: null,
      }

      this.agGrid().api.applyTransaction({
        add: [defaultTeam], addIndex: 0
      });
    }
  }

  isFormValid(): boolean {
    this.showValidationMessage = false;
    if (this.editedTeams.size > 0) {
      if (Array.from(this.editedTeams.values()).filter(x => x.Description == '' || x.AssetTeamId == null ||
        x.ProgramManagerEmployeeId == null || x.ProjectManagerEmployeeId == null || x.ProjectControlAnalystEmployeeId == null
        || x.ProjectEngineerEmployeeId == null || x.ProjectTechEmployeeId == null).length > 0) {
        this.validationMessage = 'Please populate all required fields.';
        this.showValidationMessage = true;
        return false;
      }
      else
        return true;
    }
    return true;
  }

  trackChanges(e) {
    if (e.type !== 'cellValueChanged')
      return;
    let defaultTeam: DefaultTeamsData = {
      DefaultProjectTeamId: e.data.DefaultProjectTeamId,
      Description: e.data.Description,
      AssetTeamId: e.data.AssetTeamId,
      FunctionId: e.data.FunctionId,
      ProgramManagerEmployeeId: e.data.ProgramManagerEmployeeId,
      ProjectManagerEmployeeId: e.data.ProjectManagerEmployeeId,
      ProjectControlAnalystEmployeeId: e.data.ProjectControlAnalystEmployeeId,
      ProjectEngineerEmployeeId: e.data.ProjectEngineerEmployeeId,
      ProjectTechEmployeeId: e.data.ProjectTechEmployeeId,
    }
    switch (e.colDef.field) {
      case 'Description':
        if (e.newValue === '') {
          e.node.data.Description = e.oldValue;
          this.agGrid().api.refreshCells();
          return;
        }
        break;
      case 'ProgramManagerEmployeeId':
        if (!e.newValue) {
          e.node.data.ProgramManagerEmployeeId = e.oldValue;
          this.agGrid().api.refreshCells();
          return;
        }
        break;
      case 'ProjectManagerEmployeeId':
        if (!e.newValue) {
          e.node.data.ProjectManagerEmployeeId = e.oldValue;
          this.agGrid().api.refreshCells();
          return;
        }
        break;
      case 'ProjectControlAnalystEmployeeId':
        if (!e.newValue) {
          e.node.data.ProjectControlAnalystEmployeeId = e.oldValue;
          this.agGrid().api.refreshCells();
          return;
        }
        break;
      case 'ProjectEngineerEmployeeId':
        if (!e.newValue) {
          e.node.data.ProjectEngineerEmployeeId = e.oldValue;
          this.agGrid().api.refreshCells();
          return;
        }
        break;
      case 'ProjectTechEmployeeId':
        if (!e.newValue) {
          e.node.data.ProjectTechEmployeeId = e.oldValue;
          this.agGrid().api.refreshCells();
          return;
        }
        break;
    }

    this.editedTeams.set(e.node.id, defaultTeam);
  }

  saveChanges() {
    if (this.isFormValid()) {
      this.openedWarning = true;
    }
  }

  public createFormGroup(args: any): any {
    const item = args.isNew ? new DefaultProjectTeam() : args.dataItem;
    this.formGroup = this.formBuilder.group({
      'DefaultProjectTeamId': [item.DefaultProjectTeamId || 0],
      'Description': [item.Description, Validators.required],
      'AssetTeamId': [item.AssetTeamId, Validators.required],
      'FunctionId': [item.FunctionId, Validators.required],
      'ProgramManagerEmployeeId': [item.ProgramManagerEmployeeId, Validators.required],
      'ProjectManagerEmployeeId': [item.ProjectManagerEmployeeId, Validators.required],
      'ProjectControlAnalystEmployeeId': [item.ProjectControlAnalystEmployeeId, Validators.required],
      'ProjectEngineerEmployeeId': [item.ProjectEngineerEmployeeId, Validators.required],
      'ProjectTechEmployeeId': [item.ProjectTechEmployeeId, Validators.required],
      'InUseCount': [item.InUseCount]
    });

    this.markFormGroupTouched(this.formGroup);
    return this.formGroup;
  }

  public toggleEditMode() {
    this.editMode = !this.editMode;
    if (this.new) {
      this.editMode = true;
    }
  }

  public addHandler(event) {
    this.new = true;
  }

  public closeEditMode() {
    this.closeEditor();
    this.editMode = false;
    if (this.formGroup) this.formGroup.reset();
    this.selectedKeys = [];
  }

  public closeEditor(grid: GridComponent = this.grid(), rowIndex: number = this.editedRowIndex): void {
    if (this.formGroup) {
      let contextRowIndex = rowIndex - this.gridState.skip;
      if (this.new) {
        (grid.data as any).data.splice(0, 0, this.formGroup.getRawValue());
        this.newInQueue = true;
        this.new = false;
      } else {
        (grid.data as any).data[contextRowIndex] = this.formGroup.getRawValue();
      }
      this.formGroup.reset();
    }
    this.new = false;
    this.selectedKeys = [];
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
  }

  public closeNew() {
    this.new = false;
  }

  public editHandler({ sender, rowIndex, dataItem, columnIndex }: any): void {
    if (this.formGroup && !this.formGroup.valid && this.formGroup.dirty) {
      return;
    }

    let el = (document.querySelector(`.k-grid-content.k-virtual-content`));
    let scrollLeft = el.scrollLeft;

    this.saveRow();
    this.createFormGroup({ dataItem });
    this.editedRowIndex = rowIndex;
    this.loadFilteredLists(dataItem);
    sender.editRow(rowIndex, this.formGroup);
    this.markFormGroupTouched(this.formGroup);

    //hack - kendo team needs to address this internally. In the meantime...
    setTimeout(() => {
      el.scrollLeft = scrollLeft;
    }, 1000);
  }

  public editClick({ dataItem, rowIndex, columnIndex, isEdited }: any, canEdit: boolean, editMode: boolean): void {
    if (!canEdit || !editMode) {
      return;
    }
    if (this.formGroup) {
      this.saveClick();
    }

    if (!isEdited) {
      if (this.newInQueue) { this.newInQueue = false; return; }
      this.editHandler({
        dataItem: dataItem,
        rowIndex: rowIndex,
        sender: this.grid(),
        isNew: false,
        columnIndex: columnIndex
      });
    }
  }

  public selectionChange(event: SelectionEvent) {
    event.selectedRows.forEach((row) => {
      this.selected.push(row);
    });

    event.deselectedRows.forEach((row) => {
      this.selected = this.selected.filter(f => f.index !== row.index);
    });

  }

  public saveClick(): void {
    if (this.formGroup && !this.formGroup.valid) {
      return;
    }

    this.saveRow();
  }

  public saveRow(): void {
    if (this.isInEditingMode) {
      this.projectTeamService.update(this.formGroup.value);
    }

    this.closeEditor(this.grid());
  }

  public onWarningClose() {
    this.openedWarning = false;
  }

  public clearDropDown(dropdown: string) {
    switch (dropdown) {
      case 'Program Manager': {
        this.filteredProgramMgrList = [];
        break;
      }
      case 'Project Manager': {
        this.filteredProjectMgrList = [];
        break;
      }
      case 'Project Control Analyst': {
        this.filteredProjectControlList = [];
        break;
      }
      case 'Project Engineer': {
        this.filteredProjectEngineerList = [];
        break;
      }
      case 'Project Tech': {
        this.filteredProjectTechList = [];
        break;
      }
    }
  }

  public onWarningSave() {
    this.openedWarning = false;
    this.loading = true;
    this.generalProjectService.addUpdateDefaultTeams(Array.from(this.editedTeams.values())).subscribe((res) => {
      this.loading = false;
      this.editedTeams = new Map<any, DefaultTeamsData>();
      this.loadTeams();
    });
  }

  public updateTeamWithConflict(team: any) {
    this.projectTeamService.update(new DefaultProjectTeamMaintenanceView(team));
    let defaultProjectTeamId = team.DefaultProjectTeamId;
    let resolvedIndex = this.conflictingProjects.findIndex(x => x.DefaultProjectTeamId = defaultProjectTeamId);

    //Remove Team from conflict list
    if (resolvedIndex !== -1) this.conflictingProjects.splice(resolvedIndex, 1);

    //Close Dialog if no teams are left in conflict
    if (this.conflictingProjects.length === 0) this.closeConflictsDialog();
  }

  public closeConflictsDialog() {
    this.openConflicts = false;
    this.projectTeamService.saveChanges();
  }

  public excelExportAllData(): ExcelExportData {
    let data = [];
    this.conflictingProjects.map(t => t.Projects.map(x => data.push(x)));
    const result: ExcelExportData = {
      data: process(data, { group: this.group, sort: [{ field: 'DefaultProjectTeamId', dir: 'asc' }] }).data,
      group: this.group
    };

    return result;
  }

  public handleAutoCompleteEmployeeFilter(value, role?: string) {
    if (value.length <= 2) {
      return;
    }

    switch (role) {
      case 'Program Manager': {
        this.filteredProgramMgrList = this.projectTeamService.filterEmployeesByActive(value, this.employeeList);
        break;
      }
      case 'Project Manager': {
        this.filteredProjectMgrList = this.projectTeamService.filterEmployeesByActive(value, this.employeeList);
        break;
      }
      case 'Project Control Analyst': {
        this.filteredProjectControlList = this.projectTeamService.filterEmployeesByActive(value, this.employeeList);
        break;
      }
      case 'Project Engineer': {
        this.filteredProjectEngineerList = this.projectTeamService.filterEmployeesByActive(value, this.employeeList);
        break;
      }
      case 'Project Tech': {
        this.filteredProjectTechList = this.projectTeamService.filterEmployeesByActive(value, this.employeeList);
        break;
      }
    }
    this.cdr.detectChanges();
  }

  public displayTeamDialog(msg: string) {
    this.teamDialogMessage = msg;
    this.openedTeamDialogError = true;
  }

  public closeTeamDialog() {
    this.openedTeamDialogError = false;
  }

  public handleAdminEmployeeSelect(e, roleName, dataItem) {
    // check if the Program Manager or the Project Tech are contractors
    if (!this.isNotContractor(e) && roleName === 'Program Manager') {
      this.displayTeamDialog('A contractor can not be selected for the Program Manager role');
      return;
    }
    if (!this.isNotContractor(e) && roleName === 'Project Tech') {
      this.displayTeamDialog('A contractor can not be selected for the Program Tech role');
      return;
    }
  }

  /*
      1/15/2025: 
      we are 'temporarily' removing this UI restriction due to some employees having an unexpected WorkerType value.
      I've chosen not to update the name of the function in case we desire to revert this back to truly being a check for whether or not
      the user is a contractor. 

      https://pxd.visualstudio.com/CPPE/_workitems/edit/257196
  */
  public isNotContractor(employeeId): boolean {
    if (employeeId === undefined) { return false; }

    let foundEmployee = this.employeeList.find(employee => { return employee.EmployeeId === employeeId; });

    return foundEmployee != null;

    // return (foundEmployee.WorkerType && foundEmployee.WorkerType === 'Employee') || 
    //        (foundEmployee.WorkerType && foundEmployee.WorkerType === 'Regular') || 
    //        (foundEmployee.WorkerType && foundEmployee.WorkerType === 'Exxon_Transition_Employee');
  }
}